import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  EnvelopeIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import FileDropzone from './common/FileDropzone';

function AdherentSearchSelect({
  label,
  hint,
  selected,
  onSelect,
  search,
  onSearchChange,
  candidats,
  loading,
  disabled,
}) {
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <div className="relative">
        <input
          type="text"
          value={selected ? `${selected.prenom} ${selected.nom}` : search}
          onChange={(e) => {
            onSelect(null);
            onSearchChange(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => !disabled && setShowDropdown(true)}
          disabled={disabled}
          placeholder="Rechercher par nom ou prénom..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-honey-500 focus:border-honey-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        {showDropdown && !disabled && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
            <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-52 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
              {loading ? (
                <div className="px-4 py-3 text-sm text-gray-500">Chargement...</div>
              ) : candidats.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">Aucun adhérent trouvé</div>
              ) : (
                candidats.map((c) => (
                  <button
                    key={c.userId}
                    type="button"
                    disabled={!c.selectable}
                    onClick={() => {
                      if (!c.selectable) return;
                      onSelect(c);
                      onSearchChange('');
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm ${
                      c.selectable
                        ? 'hover:bg-honey-50 text-gray-900 cursor-pointer'
                        : 'text-gray-400 cursor-not-allowed bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">{c.prenom} {c.nom}</div>
                    {!c.selectable && c.reason && (
                      <div className="text-xs text-red-500 mt-0.5">{c.reason}</div>
                    )}
                    {c.selectable && c.pouvoirsCount > 0 && (
                      <div className="text-xs text-honey-600 mt-0.5">
                        {c.pouvoirsCount} pouvoir{c.pouvoirsCount > 1 ? 's' : ''} déjà reçu{c.pouvoirsCount > 1 ? 's' : ''}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminAjouterPouvoirModal({ isOpen, onClose, reunionId, onSuccess }) {
  const [mandantSearch, setMandantSearch] = useState('');
  const [mandataireSearch, setMandataireSearch] = useState('');
  const [mandants, setMandants] = useState([]);
  const [mandataires, setMandataires] = useState([]);
  const [loadingMandants, setLoadingMandants] = useState(false);
  const [loadingMandataires, setLoadingMandataires] = useState(false);
  const [selectedMandant, setSelectedMandant] = useState(null);
  const [selectedMandataire, setSelectedMandataire] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMandantSearch('');
    setMandataireSearch('');
    setSelectedMandant(null);
    setSelectedMandataire(null);
    setPdfFile(null);
    setError('');
    setMandataires([]);
    fetchMandants('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reunionId]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => fetchMandants(mandantSearch), 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandantSearch, isOpen, reunionId]);

  useEffect(() => {
    if (!isOpen || !selectedMandant) {
      setMandataires([]);
      return;
    }
    const timer = setTimeout(() => fetchMandataires(mandataireSearch), 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandataireSearch, selectedMandant, isOpen, reunionId]);

  const fetchMandants = async (q) => {
    try {
      setLoadingMandants(true);
      const token = localStorage.getItem('token');
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/mandants-pouvoir-candidats${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMandants(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des mandants');
    } finally {
      setLoadingMandants(false);
    }
  };

  const fetchMandataires = async (q) => {
    if (!selectedMandant) return;
    try {
      setLoadingMandataires(true);
      const token = localStorage.getItem('token');
      const searchParams = new URLSearchParams({
        mandantUserId: selectedMandant.userId,
      });
      if (q) searchParams.set('q', q);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/mandataires-pouvoir-candidats?${searchParams}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMandataires(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des mandataires');
    } finally {
      setLoadingMandataires(false);
    }
  };

  const handleMandantSelect = (mandant) => {
    setSelectedMandant(mandant);
    setSelectedMandataire(null);
    setMandataireSearch('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!selectedMandant) {
      alert('Veuillez sélectionner le mandant (adhérent qui donne le pouvoir)');
      return;
    }
    if (!selectedMandataire) {
      alert('Veuillez sélectionner le mandataire');
      return;
    }
    if (!pdfFile) {
      alert('Veuillez joindre le document PDF du pouvoir');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('mandantUserId', selectedMandant.userId);
      formData.append('mandataireUserId', selectedMandataire.userId);
      formData.append('pdf', pdfFile);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/pouvoirs`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement du pouvoir');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter un pouvoir</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <AdherentSearchSelect
              label="Mandant (adhérent qui donne le pouvoir) *"
              hint="Adhérent convoqué, n'ayant pas encore donné pouvoir."
              selected={selectedMandant}
              onSelect={handleMandantSelect}
              search={mandantSearch}
              onSearchChange={setMandantSearch}
              candidats={mandants}
              loading={loadingMandants}
              disabled={false}
            />

            <AdherentSearchSelect
              label="Mandataire (adhérent qui reçoit le pouvoir) *"
              hint={`Adhérent actif ${new Date().getFullYear()}, convoqué à cette réunion.`}
              selected={selectedMandataire}
              onSelect={setSelectedMandataire}
              search={mandataireSearch}
              onSearchChange={setMandataireSearch}
              candidats={mandataires}
              loading={loadingMandataires}
              disabled={!selectedMandant}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document pouvoir (PDF) *
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Joignez le pouvoir signé par le mandant (format PDF obligatoire).
              </p>
              <FileDropzone
                onFileSelect={setPdfFile}
                acceptedTypes=".pdf,application/pdf"
              />
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <EnvelopeIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                Le mandataire sera notifié par e-mail. Le mandant sera marqué comme représenté
                dans le tableau des présences.
              </p>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !selectedMandant || !selectedMandataire || !pdfFile}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:opacity-50"
            >
              <DocumentArrowUpIcon className="h-4 w-4" />
              {submitting ? 'Enregistrement…' : 'Enregistrer le pouvoir'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
